import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import TextInput from 'components/Common/Form/Input/TextInput'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Caption from 'LuxKit/Typography/Caption'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'

const CreateForm = styled.form`
  display: grid;
  gap: ${rem(16)};
`

const FieldGroup = styled(Group)`
  margin-bottom: ${rem(8)};
`

interface Props {
  value: string
  error?: string
  isSubmitting?: boolean
  secondaryActionLabel?: string
  onChange: (value: string) => void
  onSubmit: () => void | Promise<void>
  onSecondaryAction?: () => void
  onClose: () => void
}

export function CreateWishlistModalContent({
  value,
  error,
  isSubmitting = false,
  secondaryActionLabel,
  onChange,
  onSubmit,
  onSecondaryAction,
  onClose,
}: Props) {
  const intl = useIntl()
  const secondaryActionProps =
    secondaryActionLabel && onSecondaryAction
      ? {
          children: secondaryActionLabel,
          disabled: isSubmitting,
          onClick: onSecondaryAction,
        }
      : undefined

  const handleSubmit = () => {
    const result = onSubmit()
    if (result && typeof result.then === 'function') {
      result.catch(() => undefined)
    }
  }

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'PhCJ_90FdsAyFKWySKTB',
          defaultMessage: 'Create wishlist',
        })}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <CreateForm
            onSubmit={(event) => {
              event.preventDefault()
              handleSubmit()
            }}
          >
            <FieldGroup direction="vertical" gap={4}>
              <TextInput
                autoFocus
                value={value}
                maxLength={MAX_TRIP_NAME_CHARS}
                required
                width="100%"
                label={intl.formatMessage({
                  id: '443OflF0tVJUmacN6lJE',
                  defaultMessage: 'Wishlist name',
                })}
                onChange={(event) => {
                  onChange(event.currentTarget.value)
                }}
                placeholder={intl.formatMessage({
                  id: 'iIpD97Abj0VTaDG9hzDh',
                  defaultMessage: 'e.g. Maldives trip',
                })}
                noValidationMessage
                noValidationSpacing
              />
              <Group direction="horizontal" horizontalAlign="space-between">
                <Caption as="span" variant="medium" colour="critical">
                  {error}
                </Caption>
                <Caption as="span" variant="large" colour="light-foreground-alt">
                  {intl.formatMessage(
                    {
                      id: 'lDAq329-4xx-4Jl8jmee',
                      defaultMessage: '{count}/{max}',
                    },
                    { count: value.length, max: MAX_TRIP_NAME_CHARS },
                  )}
                </Caption>
              </Group>
            </FieldGroup>
          </CreateForm>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'H3vDsSM1WwlGM5EaXLfa',
            defaultMessage: 'Create',
          }),
          disabled: !value.trim() || isSubmitting,
          onClick: handleSubmit,
        }}
        secondaryActionProps={secondaryActionProps}
      />
    </>
  )
}

function CreateWishlistModal(props: Props) {
  return (
    <ModalBase size="S" isOpen onClose={props.onClose}>
      <CreateWishlistModalContent {...props} />
    </ModalBase>
  )
}

export default CreateWishlistModal
